import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, CreditCard, MapPin, Package } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const StickerCheckoutPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [calculatedTotal, setCalculatedTotal] = useState(null);
  
  const [shippingAddress, setShippingAddress] = useState({
    name: '',
    street: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'US',
    phone: ''
  });
  
  const [notes, setNotes] = useState('');

  useEffect(() => {
    // Get items from navigation state
    if (location.state?.items) {
      setItems(location.state.items);
      calculateTotal(location.state.items, shippingAddress);
    } else {
      // No items selected, redirect back
      navigate('/order-stickers');
    }
  }, [location.state, navigate]);

  const calculateTotal = async (itemsToCalculate, address) => {
    if (!itemsToCalculate || itemsToCalculate.length === 0) return;
    
    // Only calculate if we have state (for shipping calculation)
    if (!address.state) {
      const subtotal = itemsToCalculate.reduce((sum, item) => 
        sum + (item.quantity * item.price_per_item), 0
      );
      setCalculatedTotal({
        subtotal: subtotal.toFixed(2),
        shipping_cost: 'TBD',
        total: 'TBD',
        free_shipping_eligible: subtotal >= 20
      });
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/orders/stickers/calculate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          items: itemsToCalculate.map(item => ({
            reward_id: item.reward_id,
            reward_name: item.reward_name,
            reward_type: item.reward_type,
            image_url: item.image_url,
            quantity: item.quantity,
            price_per_item: item.price_per_item
          })),
          shipping_address: {
            full_name: address.name || 'Customer',
            address_line1: address.street || 'TBD',
            address_line2: null,
            city: address.city || 'TBD',
            state: address.state,
            zip_code: address.zip_code || '00000',
            country: address.country || 'US',
            phone: address.phone || null
          },
          notes: notes
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setCalculatedTotal(data);
      } else {
        // Silently fail for calculate - user can still proceed without preview
        console.warn('Calculate endpoint failed:', response.status);
      }
    } catch (error) {
      console.error('Failed to calculate total:', error);
    }
  };

  const handleAddressChange = (field, value) => {
    const newAddress = { ...shippingAddress, [field]: value };
    setShippingAddress(newAddress);
    
    // Recalculate if state changes
    if (field === 'state' && value) {
      calculateTotal(items, newAddress);
    }
  };

  const handleCheckout = async () => {
    // Validate address
    if (!shippingAddress.name || !shippingAddress.street || !shippingAddress.city || 
        !shippingAddress.state || !shippingAddress.zip_code) {
      toast({
        title: 'Incomplete Address',
        description: 'Please fill in all required shipping address fields',
        variant: 'destructive'
      });
      return;
    }

    // Check if user is logged in
    const token = localStorage.getItem('access_token');
    if (!token) {
      toast({
        title: 'Not Logged In',
        description: 'Please log in to complete your order',
        variant: 'destructive'
      });
      // Redirect to login
      navigate('/login');
      return;
    }

    setLoading(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/orders/stickers/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          items: items.map(item => ({
            reward_id: item.reward_id,
            reward_name: item.reward_name,
            reward_type: item.reward_type,
            image_url: item.image_url,
            quantity: item.quantity,
            price_per_item: item.price_per_item
          })),
          shipping_address: {
            full_name: shippingAddress.name,
            address_line1: shippingAddress.street,
            address_line2: null,
            city: shippingAddress.city,
            state: shippingAddress.state,
            zip_code: shippingAddress.zip_code,
            country: shippingAddress.country,
            phone: shippingAddress.phone || null
          },
          notes: notes
        })
      });

      if (response.ok) {
        const data = await response.json();
        const redirectUrl = data.checkout_url || data.url; // ← support both keys
        if (!redirectUrl) {
          throw new Error('No checkout URL returned from server.');
        }
        window.location.href = redirectUrl;
      } else {
        let errorMessage = 'Checkout failed';

        // Try JSON first
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch {
          // Fall back to raw text (helps surface backend exceptions)
          try {
            const text = await response.text();
            if (text) errorMessage = `${errorMessage}: ${text}`;
          } catch {
            // ignore if even text fails
          }

          // Status-specific helpers
          if (response.status === 401) {
            errorMessage = 'Authentication failed. Please log in again.';
          } else if (response.status === 500) {
            errorMessage = 'Server error. Stripe may not be configured.';
          } else if (response.status === 503) {
            errorMessage = 'Service temporarily unavailable. Please retry.';
          } else {
            errorMessage = `${errorMessage} (HTTP ${response.status})`;
          }
        }

        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast({
        title: 'Checkout Failed',
        description: error.message || 'Failed to create checkout session',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const subtotal = calculatedTotal?.subtotal || 
    items.reduce((sum, item) => sum + (item.quantity * item.price_per_item), 0).toFixed(2);
  
  const shipping = calculatedTotal?.shipping_cost || 'TBD';
  const total = calculatedTotal?.total || 'TBD';

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/order-stickers')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Selection
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Checkout</h1>
            <p className="text-slate-600">Complete your order</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Address & Notes */}
          <div className="lg:col-span-2 space-y-6">
            {/* Shipping Address */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Shipping Address
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    value={shippingAddress.name}
                    onChange={(e) => handleAddressChange('name', e.target.value)}
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <Label htmlFor="street">Street Address *</Label>
                  <Input
                    id="street"
                    value={shippingAddress.street}
                    onChange={(e) => handleAddressChange('street', e.target.value)}
                    placeholder="123 Main St, Apt 4B"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      value={shippingAddress.city}
                      onChange={(e) => handleAddressChange('city', e.target.value)}
                      placeholder="New York"
                    />
                  </div>
                  <div>
                    <Label htmlFor="state">State *</Label>
                    <Input
                      id="state"
                      value={shippingAddress.state}
                      onChange={(e) => handleAddressChange('state', e.target.value.toUpperCase())}
                      placeholder="NY"
                      maxLength={2}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="zip">ZIP Code *</Label>
                    <Input
                      id="zip"
                      value={shippingAddress.zip_code}
                      onChange={(e) => handleAddressChange('zip_code', e.target.value)}
                      placeholder="10001"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone (optional)</Label>
                    <Input
                      id="phone"
                      value={shippingAddress.phone}
                      onChange={(e) => handleAddressChange('phone', e.target.value)}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>

                <div>
                  <Label>Country</Label>
                  <Input value="United States" disabled />
                  <p className="text-xs text-slate-500 mt-1">
                    Currently shipping to US only
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Order Notes */}
            <Card>
              <CardHeader>
                <CardTitle>Order Notes (Optional)</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any special instructions or notes for your order..."
                  rows={3}
                />
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Order Summary */}
          <div className="lg:col-span-1">
            <Card className="sticky top-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Order Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Items List */}
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {items.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-2 bg-slate-50 rounded">
                      <div className="w-12 h-12 bg-white rounded flex items-center justify-center overflow-hidden flex-shrink-0">
                        {item.image_url ? (
                          <img
                            src={item.image_url}
                            alt={item.reward_name}
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <span className="text-2xl">🎁</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold truncate">{item.reward_name}</p>
                        <p className="text-xs text-slate-600">Qty: {item.quantity}</p>
                      </div>
                      <div className="text-sm font-semibold">
                        ${(item.quantity * item.price_per_item).toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Totals */}
                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Subtotal:</span>
                    <span className="font-semibold">${subtotal}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Shipping:</span>
                    <span className={`font-semibold ${
                      calculatedTotal?.free_shipping_eligible ? 'text-green-600' : ''
                    }`}>
                      {shipping === 'TBD' ? 'Enter state' : 
                       shipping === 0 ? 'FREE' : `$${shipping}`}
                    </span>
                  </div>
                  {calculatedTotal?.free_shipping_eligible && (
                    <Badge className="w-full justify-center bg-green-500">
                      Free Shipping! 🎉
                    </Badge>
                  )}
                  <div className="flex justify-between font-bold text-lg pt-2 border-t">
                    <span>Total:</span>
                    <span>${total}</span>
                  </div>
                </div>

                {/* Checkout Button */}
                <Button
                  onClick={handleCheckout}
                  disabled={loading || !shippingAddress.state}
                  className="w-full"
                  size="lg"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  {loading ? 'Processing...' : 'Proceed to Payment'}
                </Button>

                <div className="text-xs text-slate-500 space-y-1">
                  <p>• Secure payment via Stripe</p>
                  <p>• Processing: 2-3 business days</p>
                  <p>• Shipping: 3-5 business days</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StickerCheckoutPage;
